import { HTTP_SKIP_GLOBAL_LOADING } from '@core/interceptors/http-skip-global-loading.token';
import {
  isHttpNetworkError,
  resolveApiHealthUrl
} from '@core/services/network/server-reachability.util';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';

const UNREACHABLE_DEBOUNCE_MS = 120;

/**
 * Stato “server API non raggiungibile” guidato da eventi HTTP (interceptor + Riprova one-shot).
 * Nessun polling. Vedi `docs/NETWORK_CONNECTIVITY.md`.
 */
@Injectable({
  providedIn: 'root'
})
export class ServerReachabilityService {
  private readonly http = inject(HttpClient);

  private readonly isUnreachableSignal = signal(false);
  private readonly retryingSignal = signal(false);
  private unreachableDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** True se l'origin di `API_ENDPOINT` risulta irraggiungibile. */
  readonly isUnreachable = this.isUnreachableSignal.asReadonly();

  /** True mentre è in corso il `retryCheck()` one-shot. */
  readonly retrying = this.retryingSignal.asReadonly();

  /**
   * Segnala irraggiungibilità (debounce breve per burst di errori paralleli).
   */
  markUnreachable(): void {
    if (this.unreachableDebounceTimer != null) {
      return;
    }
    this.unreachableDebounceTimer = setTimeout(() => {
      this.unreachableDebounceTimer = null;
      this.isUnreachableSignal.set(true);
      console.log('[ServerReachabilityService] unreachable');
    }, UNREACHABLE_DEBOUNCE_MS);
  }

  /**
   * Segnala che il server ha risposto (qualsiasi status HTTP > 0).
   */
  markReachable(): void {
    if (this.unreachableDebounceTimer != null) {
      clearTimeout(this.unreachableDebounceTimer);
      this.unreachableDebounceTimer = null;
    }
    if (this.isUnreachableSignal()) {
      console.log('[ServerReachabilityService] reachable');
    }
    this.isUnreachableSignal.set(false);
  }

  /**
   * One-shot health `GET /up` (solo azione utente «Riprova»).
   * Skip loading globale.
   */
  retryCheck(): Observable<unknown> {
    const url = resolveApiHealthUrl(environment.API_ENDPOINT);
    const context = new HttpContext().set(HTTP_SKIP_GLOBAL_LOADING, true);
    this.retryingSignal.set(true);

    return this.http.get(url, { context, responseType: 'text', observe: 'response' }).pipe(
      tap(() => this.markReachable()),
      catchError((error: { status?: number; error?: unknown; name?: string }) => {
        if (isHttpNetworkError(error)) {
          this.markUnreachable();
        } else if (typeof error.status === 'number' && error.status > 0) {
          this.markReachable();
        }
        return throwError(() => error);
      }),
      finalize(() => this.retryingSignal.set(false))
    );
  }
}
